<template>
  <PageLayout>
    <template slot="header">
      <div class="d-flex m-3 mx-lg-0">
        <Breadcrumb :list="breadcrumbList" />
        <ExportData
          id="exportData"
          class="ml-sm-2 mt-2 mt-sm-0"
          :canExport="$can('use', 'report.export')"
          :hasXlsx="true"
          :hasCsv="false"
          @export="exportFile"
        />
        <GroupsTreeSelect class="ml-md-1" @selectItem="selectGroupItem" />
      </div>
    </template>
    <template slot="main">
      <div class="page-culture">
        <b-row>
          <b-col xl="4" class="mb-4">
            <b-card class=" mb-4 maturity-level">
              <header slot="header">
                <b-row>
                  <b-col :title="labels.maturityLevel.title" md="12">
                    <div class="title">{{ labels.maturityLevel.title }}</div>
                  </b-col>
                </b-row>
              </header>
              <div class="text-center" v-if="loading">
                <loading />
                <p>{{ labels.loading }}</p>
              </div>
              <h3 class="main-percentage" v-else>
                {{ maturityLevel ? maturityLevel.percentage + '%' : '- -' }}
              </h3>

              <div class="details" v-if="maturityLevel && !loading">
                <h5 v-if="maturityLevel">
                  {{
                    labels.maturityLevel.currentLevel
                      | parse({
                        MATURITY: maturityLevel.level,
                      })
                  }}
                </h5>
                <h5 v-else>
                  {{ labels.maturityLevel.currentLevelEmpty }}
                </h5>
                <h6 v-if="maturityLevel && maturityLevel.desiredLevel">
                  {{
                    labels.maturityLevel.desiredLevel
                      | parse({
                        DESIRED: maturityLevel.desiredLevel,
                      })
                  }}
                </h6>
                <h6 v-else>
                  {{ labels.maturityLevel.desiredLevelEmpty }}
                </h6>
              </div>

              <div class="bar-levels" v-if="maturityLevel && !loading">
                <div
                  class="marker"
                  :style="{ left: maturityLevel.percentage + '%' }"
                ></div>
                <div class="levels">
                  <div class="level one"></div>
                  <div class="level two"></div>
                  <div class="level three"></div>
                  <div class="level four"></div>
                  <div class="level five"></div>
                </div>
                <div class="scale">
                  <span class="value">0</span>
                  <span class="value">50</span>
                  <span class="value">100</span>
                </div>
              </div>
            </b-card>
          </b-col>
          <b-col class="mb-4">
            <b-card class="culture-grow">
              <header slot="header">
                <b-row>
                  <b-col :title="labels.culturalEvolution" md="12">
                    {{ labels.culturalEvolution }}
                    <p class="ranking-date">
                      <span v-if="date && !loading">
                        {{
                          labels.date
                            | parse({
                              DATE: $moment(date).format('DD/MM/YYYY'),
                            })
                        }}
                      </span>
                      <span class="ranking-generation">
                        {{ labels.rankingGeneration }}
                      </span>
                    </p>
                  </b-col>
                </b-row>
              </header>
              <div class="text-center" v-if="loading">
                <loading />
                <p>{{ labels.loading }}</p>
              </div>
              <div
                class="text-center empty-state-box"
                v-else-if="!history.length"
              >
                <video muted loop autoplay src="home/empty-state-users.mp4" />
                <p class="mt-3">{{ labels.empty | parse() }}</p>
              </div>
              <b-row v-else>
                <b-col md="12">
                  <VueApexCharts
                    type="line"
                    height="350px"
                    :options="culturalEvolutionOptions"
                    :series="culturalEvolution"
                  />
                </b-col>
              </b-row>
            </b-card>
          </b-col>
        </b-row>

        <b-card>
          <header slot="header">
            <b-row>
              <b-col :title="labels.header" md="12">
                {{ labels.header }}
                <div class="float-right">
                  <small class="order-by">{{ labels.orderBy }}</small>
                  <b-button-group class="toggle-sort">
                    <b-button
                      class="mr-2"
                      :title="`${labels.orderBy} ${labels.probability}`"
                      @click="toggleProbabilitySort(true)"
                      :variant="isProbabilitySort ? 'primary' : 'link'"
                    >
                      {{ labels.probability }}
                    </b-button>
                    <b-button
                      :title="`${labels.orderBy} ${labels.votes}`"
                      @click="toggleProbabilitySort(false)"
                      :variant="!isProbabilitySort ? 'primary' : 'link'"
                    >
                      {{ labels.votes }}
                    </b-button>
                  </b-button-group>
                </div>
              </b-col>
            </b-row>
          </header>
          <div>
            <div class="text-center" v-if="loading">
              <loading />
              <p>{{ labels.loading }}</p>
            </div>
            <DashboardCards
              v-if="!loading"
              :activeCard="activeCard"
              :culturalEntropy="culturalEntropy"
              :culturalAlignment="culturalAlignment"
              :showCulturalAlignment="
                culturalAlignment && culturalAlignment.totalFavorable >= 0
              "
              :votes="votes"
              @changeCard="changeCard"
              @showModal="showDesiredValuesModal"
            />
            <div
              class="text-center empty-state-box"
              v-if="!ranking.length && !loading"
            >
              <video muted loop autoplay src="home/empty-state-users.mp4" />
              <p class="mt-3">{{ labels.empty | parse() }}</p>
            </div>
            <div class="votes-flex" v-if="ranking.length > 0 && !loading">
              <b-card
                @click="showDescription(culture)"
                v-for="culture in filterRanking"
                :key="`card-${culture.position}`"
                class="value-card"
                :img-src="imageUrl.replace('$ID$', culture.id)"
                img-alt="Image"
                img-top
              >
                <div v-if="culture.desired" class="desired-value">
                  <b-badge variant="tertiary" :title="culture.desiredValue">
                    {{ culture.desiredValue }}
                  </b-badge>
                </div>
                <img
                  v-if="culture.unfavorable"
                  class="value-image"
                  src="../../assets/culture/limiting.svg"
                />
                <div
                  v-if="culture.reliabilityRate"
                  class="text-center reliability-rate-badge"
                >
                  <b-badge :class="culture.reliabilityRate">
                    {{ labels.scaleLabels[culture.reliabilityRate] }}
                  </b-badge>
                </div>
                <div class="container">
                  <p class="text-center value-label">
                    {{ culture.label }}
                  </p>
                  <span class="float-left">
                    <i
                      v-if="
                        isProbabilitySort
                          ? culture.positionProbability <= 10
                          : culture.position <= 10
                      "
                      class="material-icons text-warning"
                    >
                      emoji_events
                    </i>
                    {{
                      isProbabilitySort
                        ? culture.positionProbability
                        : culture.position
                    }}&deg;
                  </span>
                  <span
                    class="float-right"
                    :class="{ 'text-gray01': isProbabilitySort }"
                  >
                    <small>
                      {{ culture.votes }}
                      {{ culture.votes === 1 ? labels.vote : labels.votes }}
                    </small>
                  </span>
                  <span class="float-right mr-1 ml-1">|</span>
                  <span
                    class="float-right"
                    :class="{ 'text-gray01': !isProbabilitySort }"
                  >
                    <small>P {{ culture.probability }}%</small>
                  </span>
                </div>
              </b-card>
            </div>
          </div>
        </b-card>
        <DesiredValuesModal
          ref="desired-values-modal"
          :loading="loadingDesiredValues"
          :imageUrl="imageUrl"
          :desiredValues="desiredValues"
          :values="values.filter(v => !v.unfavorable)"
          @addDesiredValue="$store.dispatch('culture/addDesiredValue')"
          @toggleVisibilityDesiredValue="toggleVisibilityDesiredValue"
          @changeDesiredValueName="changeDesiredValueName"
          @removeDesiredValue="removeDesiredValue"
          @saveDesiredValue="saveDesiredValue"
          @updateDesiredValue="updateDesiredValue"
          @selectValuesChanged="selectValuesChanged"
        />
        <ValuesModal
          ref="values-modal"
          :imageUrl="imageUrl"
          :selectedCulture="selectedCulture"
          :isProbabilitySort="isProbabilitySort"
        />
      </div>
      <modal-plans
        ref="component-modal-plans"
        :title="this.labels.breadcrumb"
        :hideHeaderClose="true"
        :visible="true"
        v-if="!$can('use', 'culture') || isBasicPlan"
        @hide="handleHide"
      />
    </template>
  </PageLayout>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import { mapGetters } from 'vuex'
import Breadcrumb from '@/components/Shared/Breadcrumb.vue'
import GroupsTreeSelect from '@/components/Groups/GroupsTreeSelect.vue'
import PageLayout from '@/components/Home/PageLayout.vue'
import DashboardCards from '@/components/Culture/DashboardCards.vue'
import ValuesModal from '@/components/Culture/ValuesModal.vue'
import DesiredValuesModal from '@/components/Culture/DesiredValuesModal.vue'
import Loading from '@/components/Shared/Loading.vue'
import { getUser, isBasicPlan } from '@/service/user'
import ModalPlans from '@/components/Modal/ModalPlans.vue'
import ExportData from '@/components/Reports/ExportData.vue'

const sortByProbability = (
  { positionProbability: pA },
  { positionProbability: pB }
) => {
  if (pA < pB) return -1
  if (pA > pB) return 1
  return 0
}

export default {
  components: {
    Breadcrumb,
    GroupsTreeSelect,
    PageLayout,
    Loading,
    VueApexCharts,
    DashboardCards,
    ValuesModal,
    DesiredValuesModal,
    ModalPlans,
    ExportData,
  },
  data() {
    return {
      isProbabilitySort: false,
      activeCard: 0,
      loading: false,
      selectedCulture: {},
      imageUrl: 'https://static.teamculture.com.br/images/culture/$ID$.svg',
    }
  },
  computed: {
    ...mapGetters({
      resources: 'resources',
      ranking: 'culture/ranking',
      date: 'culture/date',
      culturalEntropy: 'culture/culturalEntropy',
      culturalAlignment: 'culture/culturalAlignment',
      maturityLevel: 'culture/maturityLevel',
      history: 'culture/history',
      historyDates: 'culture/historyDates',
      values: 'culture/values',
      desiredValues: 'culture/desiredValues',
      votes: 'culture/votes',
      loadingDesiredValues: 'culture/loadingDesiredValues',
      selectedGroup: 'user/selectedGroup',
    }),
    labels() {
      return this.resources.page.home.culture
    },
    breadcrumbList() {
      return [
        {
          label: this.resources.general.menu.culture,
        },
        {
          label: this.labels.breadcrumb,
        },
      ]
    },
    groupId() {
      return this.$route.params.groupId
    },
    user() {
      return getUser()
    },
    isBasicPlan() {
      return isBasicPlan()
    },
    filterRanking() {
      const sortedRanking = this.isProbabilitySort
        ? this.ranking.map(v => ({ ...v })).sort(sortByProbability)
        : this.ranking

      switch (this.activeCard) {
        case 0:
          return sortedRanking
        case 1:
          return sortedRanking.filter(i => i.desired)
        case 2:
          return sortedRanking.filter(i => i.unfavorable)
        default:
          return null
      }
    },
    culturalEvolutionOptions() {
      return {
        chart: {
          type: 'line',
          zoom: {
            enabled: false,
          },
          toolbar: {
            show: false,
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          width: 2,
          curve: 'straight',
          dashArray: 2,
        },
        legend: {
          horizontalAlign: 'left',
          offsetX: 5,
          offsetY: -10,
        },
        markers: {
          size: 4,
          hover: {
            sizeOffset: 5,
          },
        },
        colors: this.dynamicColors,
        yaxis: {
          labels: {
            formatter: value => {
              return `${value}%`
            },
          },
        },
        xaxis: {
          type: 'datetime',
          categories: this.historyDates,
          labels: {
            format: 'dd/MM/yyyy',
            style: {
              fontSize: '10px',
            },
          },
        },
        grid: {
          borderColor: '#f1f1f1',
        },
        tooltip: {
          y: {
            formatter: value => {
              if (value === null) {
                return 'N/A'
              }
              return `${value}%`
            },
          },
          x: {
            format: 'dd/MM/yyyy',
          },
        },
      }
    },
    culturalEvolution() {
      const series = [
        'maturityLevel',
        'culturalAlignment',
        'culturalEntropy',
      ].reduce((list, metric) => {
        list.push(...this.history.filter(i => i.id === metric))
        return list
      }, [])
      return series.map(({ id, data }) => ({
        id,
        name: this.labels.legend[id],
        data,
      }))
    },
    dynamicColors() {
      const colors = {
        maturityLevel: '#177ee3',
        culturalAlignment: '#28a745',
        culturalEntropy: '#eb235e',
      }
      return this.culturalEvolution.map(s => colors[s.id])
    },
  },
  methods: {
    toggleProbabilitySort(value) {
      this.isProbabilitySort = value
    },
    exportFile(type) {
      // eslint-disable-next-line no-undef
      const env = window.env || process.env
      const groupId =
        this.groupId === 'company' ? this.user.companyId : this.groupId

      window.open(
        `${env.VUE_APP_EXPORTER_API_HOST}/culture/${groupId}/${type}?token=${localStorage.jwt}&language=${localStorage.languageabrev}`,
        '_blank'
      )
    },
    async loadRanking(groupId) {
      this.loading = true
      const requests = this.$can('use', 'culture')
        ? [
            this.$store.dispatch('culture/loadRanking', { groupId }),
            this.$store.dispatch('culture/loadRankingHistory', { groupId }),
          ]
        : []
      await Promise.all(requests)
      this.loading = false
    },
    showDescription(culture) {
      this.selectedCulture = culture
      this.selectedCulture.series = [
        this.history.find(i => i.id === culture.id),
      ]
      this.selectedCulture.options = { ...this.culturalEvolutionOptions }
      this.selectedCulture.options.colors = [
        culture.unfavorable ? '#EB235E' : '#28a745',
      ]

      this.selectedCulture.options.tooltip.y.formatter = value => {
        return `${value} ${value === 1 ? this.labels.vote : this.labels.votes}`
      }

      this.selectedCulture.options.yaxis.labels.formatter = value => {
        return value.toFixed(1)
      }

      this.$refs['values-modal'].show()
    },
    selectGroupItem(group) {
      const groupId = group._id
      this.$router.push({ name: 'culture', params: { groupId } })
      this.loadRanking(groupId)
    },
    updateRanking() {
      this.loadRanking(this.groupId)
    },
    changeCard(card) {
      this.activeCard = card
    },
    showDesiredValuesModal() {
      const requests = [
        this.$store.dispatch('culture/loadValues'),
        this.$store.dispatch('culture/loadDesiredValues'),
      ]
      Promise.all(requests)
      this.$refs['desired-values-modal'].show()
    },
    toggleVisibilityDesiredValue(idx) {
      this.$store.dispatch('culture/toggleVisibilityDesiredValue', { idx })
    },
    changeDesiredValueName(value) {
      this.$store.dispatch('culture/changeDesiredValueName', value)
    },
    removeDesiredValue(value) {
      this.$store.dispatch('culture/removeDesiredValue', value)
    },
    saveDesiredValue(value) {
      this.$store.dispatch('culture/saveDesiredValue', value)
    },
    updateDesiredValue(value) {
      this.$store.dispatch('culture/updateDesiredValue', value)
    },
    selectValuesChanged({ values, idx }) {
      this.$store.dispatch('culture/changeSelectedValues', { values, idx })
    },
    handleHide() {
      this.$router.push({ name: 'dashboard' })
    },
  },
  created() {
    this.$root.$emit('setPageTitle', { context: this, path: 'culture' })
    const selectedGroup = this.selectedGroup
    if (selectedGroup && selectedGroup !== this.groupId) {
      this.loadRanking(selectedGroup)
      return this.$router.push({
        name: 'culture',
        params: { groupId: selectedGroup },
      })
    }
    this.loadRanking(this.groupId)
  },
}
</script>

<style lang="scss">
.maturity-level {
  height: 100%;
  header {
    height: 56px;
  }
  .card-body {
    padding: 2rem;
  }
  .title {
    font-size: 24px;
    text-align: center;
  }
}

.main-percentage {
  font-size: 50px;
  margin: 40px 30px;
  text-align: center;
}
.details {
  margin-bottom: 50px;
  h5 {
    font-size: 17px;
    line-height: 15px;
  }
  h6 {
    font-size: 14px;
    line-height: 13px;
    color: rgb(160, 160, 160);
    strong {
      color: #000;
    }
  }
}

.bar-levels {
  position: relative;
  .marker {
    width: 1px;
    height: 50px;
    border: 1px dashed #000;
    position: absolute;
    left: 0;
    top: -9px;
  }
  .levels {
    display: flex;
    height: 30px;
    width: 100%;
    .level {
      height: 100%;
      width: 20%;
      &:first-child {
        border-radius: 10px 0 0 10px;
      }
      &:last-child {
        border-radius: 0 10px 10px 0;
      }
      &.one {
        background-color: #e7342b;
      }
      &.two {
        background-color: #f89220;
      }
      &.three {
        background-color: #fada57;
      }
      &.four {
        background-color: #64dec9;
      }
      &.five {
        background-color: #1c95ba;
      }
    }
  }
}

.scale {
  display: flex;
  width: 100%;
  justify-content: space-between;
  .value {
    font-size: 13px;
  }
}

.content-culture-description {
  border-radius: 10px;
  -webkit-border-radius: 10px;
  -moz-border-radius: 10px;
  .value-icon-container {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 2000 1500'%3E%3Cdefs%3E%3Crect stroke='%23ffffff' stroke-width='.5' width='1' height='1' id='s'/%3E%3Cpattern id='a' width='3' height='3' patternUnits='userSpaceOnUse' patternTransform='scale(50) translate(-980 -735)'%3E%3Cuse fill='%23fafafa' href='%23s' y='2'/%3E%3Cuse fill='%23fafafa' href='%23s' x='1' y='2'/%3E%3Cuse fill='%23f5f5f5' href='%23s' x='2' y='2'/%3E%3Cuse fill='%23f5f5f5' href='%23s'/%3E%3Cuse fill='%23f0f0f0' href='%23s' x='2'/%3E%3Cuse fill='%23f0f0f0' href='%23s' x='1' y='1'/%3E%3C/pattern%3E%3Cpattern id='b' width='7' height='11' patternUnits='userSpaceOnUse' patternTransform='scale(50) translate(-980 -735)'%3E%3Cg fill='%23ebebeb'%3E%3Cuse href='%23s'/%3E%3Cuse href='%23s' y='5' /%3E%3Cuse href='%23s' x='1' y='10'/%3E%3Cuse href='%23s' x='2' y='1'/%3E%3Cuse href='%23s' x='2' y='4'/%3E%3Cuse href='%23s' x='3' y='8'/%3E%3Cuse href='%23s' x='4' y='3'/%3E%3Cuse href='%23s' x='4' y='7'/%3E%3Cuse href='%23s' x='5' y='2'/%3E%3Cuse href='%23s' x='5' y='6'/%3E%3Cuse href='%23s' x='6' y='9'/%3E%3C/g%3E%3C/pattern%3E%3Cpattern id='h' width='5' height='13' patternUnits='userSpaceOnUse' patternTransform='scale(50) translate(-980 -735)'%3E%3Cg fill='%23ebebeb'%3E%3Cuse href='%23s' y='5'/%3E%3Cuse href='%23s' y='8'/%3E%3Cuse href='%23s' x='1' y='1'/%3E%3Cuse href='%23s' x='1' y='9'/%3E%3Cuse href='%23s' x='1' y='12'/%3E%3Cuse href='%23s' x='2'/%3E%3Cuse href='%23s' x='2' y='4'/%3E%3Cuse href='%23s' x='3' y='2'/%3E%3Cuse href='%23s' x='3' y='6'/%3E%3Cuse href='%23s' x='3' y='11'/%3E%3Cuse href='%23s' x='4' y='3'/%3E%3Cuse href='%23s' x='4' y='7'/%3E%3Cuse href='%23s' x='4' y='10'/%3E%3C/g%3E%3C/pattern%3E%3Cpattern id='c' width='17' height='13' patternUnits='userSpaceOnUse' patternTransform='scale(50) translate(-980 -735)'%3E%3Cg fill='%23e5e5e5'%3E%3Cuse href='%23s' y='11'/%3E%3Cuse href='%23s' x='2' y='9'/%3E%3Cuse href='%23s' x='5' y='12'/%3E%3Cuse href='%23s' x='9' y='4'/%3E%3Cuse href='%23s' x='12' y='1'/%3E%3Cuse href='%23s' x='16' y='6'/%3E%3C/g%3E%3C/pattern%3E%3Cpattern id='d' width='19' height='17' patternUnits='userSpaceOnUse' patternTransform='scale(50) translate(-980 -735)'%3E%3Cg fill='%23ffffff'%3E%3Cuse href='%23s' y='9'/%3E%3Cuse href='%23s' x='16' y='5'/%3E%3Cuse href='%23s' x='14' y='2'/%3E%3Cuse href='%23s' x='11' y='11'/%3E%3Cuse href='%23s' x='6' y='14'/%3E%3C/g%3E%3Cg fill='%23e0e0e0'%3E%3Cuse href='%23s' x='3' y='13'/%3E%3Cuse href='%23s' x='9' y='7'/%3E%3Cuse href='%23s' x='13' y='10'/%3E%3Cuse href='%23s' x='15' y='4'/%3E%3Cuse href='%23s' x='18' y='1'/%3E%3C/g%3E%3C/pattern%3E%3Cpattern id='e' width='47' height='53' patternUnits='userSpaceOnUse' patternTransform='scale(50) translate(-980 -735)'%3E%3Cg fill='%23F60'%3E%3Cuse href='%23s' x='2' y='5'/%3E%3Cuse href='%23s' x='16' y='38'/%3E%3Cuse href='%23s' x='46' y='42'/%3E%3Cuse href='%23s' x='29' y='20'/%3E%3C/g%3E%3C/pattern%3E%3Cpattern id='f' width='59' height='71' patternUnits='userSpaceOnUse' patternTransform='scale(50) translate(-980 -735)'%3E%3Cg fill='%23F60'%3E%3Cuse href='%23s' x='33' y='13'/%3E%3Cuse href='%23s' x='27' y='54'/%3E%3Cuse href='%23s' x='55' y='55'/%3E%3C/g%3E%3C/pattern%3E%3Cpattern id='g' width='139' height='97' patternUnits='userSpaceOnUse' patternTransform='scale(50) translate(-980 -735)'%3E%3Cg fill='%23F60'%3E%3Cuse href='%23s' x='11' y='8'/%3E%3Cuse href='%23s' x='51' y='13'/%3E%3Cuse href='%23s' x='17' y='73'/%3E%3Cuse href='%23s' x='99' y='57'/%3E%3C/g%3E%3C/pattern%3E%3C/defs%3E%3Crect fill='url(%23a)' width='100%25' height='100%25'/%3E%3Crect fill='url(%23b)' width='100%25' height='100%25'/%3E%3Crect fill='url(%23h)' width='100%25' height='100%25'/%3E%3Crect fill='url(%23c)' width='100%25' height='100%25'/%3E%3Crect fill='url(%23d)' width='100%25' height='100%25'/%3E%3Crect fill='url(%23e)' width='100%25' height='100%25'/%3E%3Crect fill='url(%23f)' width='100%25' height='100%25'/%3E%3Crect fill='url(%23g)' width='100%25' height='100%25'/%3E%3C/svg%3E");
  }
}
</style>

<style scoped lang="scss">
@import '~@/styles/custom/colors';

header {
  .d-flex {
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
  }
}

.empty-state-box {
  overflow-y: hidden;
  padding: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-height: calc(100vh - 350px);
  text-align: center;
  width: 100%;

  video {
    text-align: center;
    min-height: 130px;
    max-height: 500px;
    max-width: 100%;
  }

  p {
    color: map-get($theme-colors, 'gray03');
    font-size: large;
  }
}

.page-culture {
  margin-bottom: 40px;
  color: map-get($theme-colors, 'primary') !important;

  .ranking-date {
    font-size: 10pt;
    margin-bottom: 0px;

    .ranking-generation {
      font-style: italic;
    }
  }

  .votes-flex {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;

    .value-image {
      position: absolute;
      height: 20px !important;
      left: 160px;
      top: 0px;
    }

    .desired-value {
      position: absolute;
      left: 0px;
      top: 15px;

      .badge {
        width: 208px;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      }
    }

    .value-card {
      cursor: pointer;
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2);
      transition: 0.3s;
      width: 210px;
      margin: 10px;
      font-size: 14pt !important;
      border-radius: 10px;
      -webkit-border-radius: 10px;
      -moz-border-radius: 10px;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 2000 1500'%3E%3Cdefs%3E%3Crect stroke='%23ffffff' stroke-width='.5' width='1' height='1' id='s'/%3E%3Cpattern id='a' width='3' height='3' patternUnits='userSpaceOnUse' patternTransform='scale(50) translate(-980 -735)'%3E%3Cuse fill='%23fafafa' href='%23s' y='2'/%3E%3Cuse fill='%23fafafa' href='%23s' x='1' y='2'/%3E%3Cuse fill='%23f5f5f5' href='%23s' x='2' y='2'/%3E%3Cuse fill='%23f5f5f5' href='%23s'/%3E%3Cuse fill='%23f0f0f0' href='%23s' x='2'/%3E%3Cuse fill='%23f0f0f0' href='%23s' x='1' y='1'/%3E%3C/pattern%3E%3Cpattern id='b' width='7' height='11' patternUnits='userSpaceOnUse' patternTransform='scale(50) translate(-980 -735)'%3E%3Cg fill='%23ebebeb'%3E%3Cuse href='%23s'/%3E%3Cuse href='%23s' y='5' /%3E%3Cuse href='%23s' x='1' y='10'/%3E%3Cuse href='%23s' x='2' y='1'/%3E%3Cuse href='%23s' x='2' y='4'/%3E%3Cuse href='%23s' x='3' y='8'/%3E%3Cuse href='%23s' x='4' y='3'/%3E%3Cuse href='%23s' x='4' y='7'/%3E%3Cuse href='%23s' x='5' y='2'/%3E%3Cuse href='%23s' x='5' y='6'/%3E%3Cuse href='%23s' x='6' y='9'/%3E%3C/g%3E%3C/pattern%3E%3Cpattern id='h' width='5' height='13' patternUnits='userSpaceOnUse' patternTransform='scale(50) translate(-980 -735)'%3E%3Cg fill='%23ebebeb'%3E%3Cuse href='%23s' y='5'/%3E%3Cuse href='%23s' y='8'/%3E%3Cuse href='%23s' x='1' y='1'/%3E%3Cuse href='%23s' x='1' y='9'/%3E%3Cuse href='%23s' x='1' y='12'/%3E%3Cuse href='%23s' x='2'/%3E%3Cuse href='%23s' x='2' y='4'/%3E%3Cuse href='%23s' x='3' y='2'/%3E%3Cuse href='%23s' x='3' y='6'/%3E%3Cuse href='%23s' x='3' y='11'/%3E%3Cuse href='%23s' x='4' y='3'/%3E%3Cuse href='%23s' x='4' y='7'/%3E%3Cuse href='%23s' x='4' y='10'/%3E%3C/g%3E%3C/pattern%3E%3Cpattern id='c' width='17' height='13' patternUnits='userSpaceOnUse' patternTransform='scale(50) translate(-980 -735)'%3E%3Cg fill='%23e5e5e5'%3E%3Cuse href='%23s' y='11'/%3E%3Cuse href='%23s' x='2' y='9'/%3E%3Cuse href='%23s' x='5' y='12'/%3E%3Cuse href='%23s' x='9' y='4'/%3E%3Cuse href='%23s' x='12' y='1'/%3E%3Cuse href='%23s' x='16' y='6'/%3E%3C/g%3E%3C/pattern%3E%3Cpattern id='d' width='19' height='17' patternUnits='userSpaceOnUse' patternTransform='scale(50) translate(-980 -735)'%3E%3Cg fill='%23ffffff'%3E%3Cuse href='%23s' y='9'/%3E%3Cuse href='%23s' x='16' y='5'/%3E%3Cuse href='%23s' x='14' y='2'/%3E%3Cuse href='%23s' x='11' y='11'/%3E%3Cuse href='%23s' x='6' y='14'/%3E%3C/g%3E%3Cg fill='%23e0e0e0'%3E%3Cuse href='%23s' x='3' y='13'/%3E%3Cuse href='%23s' x='9' y='7'/%3E%3Cuse href='%23s' x='13' y='10'/%3E%3Cuse href='%23s' x='15' y='4'/%3E%3Cuse href='%23s' x='18' y='1'/%3E%3C/g%3E%3C/pattern%3E%3Cpattern id='e' width='47' height='53' patternUnits='userSpaceOnUse' patternTransform='scale(50) translate(-980 -735)'%3E%3Cg fill='%23F60'%3E%3Cuse href='%23s' x='2' y='5'/%3E%3Cuse href='%23s' x='16' y='38'/%3E%3Cuse href='%23s' x='46' y='42'/%3E%3Cuse href='%23s' x='29' y='20'/%3E%3C/g%3E%3C/pattern%3E%3Cpattern id='f' width='59' height='71' patternUnits='userSpaceOnUse' patternTransform='scale(50) translate(-980 -735)'%3E%3Cg fill='%23F60'%3E%3Cuse href='%23s' x='33' y='13'/%3E%3Cuse href='%23s' x='27' y='54'/%3E%3Cuse href='%23s' x='55' y='55'/%3E%3C/g%3E%3C/pattern%3E%3Cpattern id='g' width='139' height='97' patternUnits='userSpaceOnUse' patternTransform='scale(50) translate(-980 -735)'%3E%3Cg fill='%23F60'%3E%3Cuse href='%23s' x='11' y='8'/%3E%3Cuse href='%23s' x='51' y='13'/%3E%3Cuse href='%23s' x='17' y='73'/%3E%3Cuse href='%23s' x='99' y='57'/%3E%3C/g%3E%3C/pattern%3E%3C/defs%3E%3Crect fill='url(%23a)' width='100%25' height='100%25'/%3E%3Crect fill='url(%23b)' width='100%25' height='100%25'/%3E%3Crect fill='url(%23h)' width='100%25' height='100%25'/%3E%3Crect fill='url(%23c)' width='100%25' height='100%25'/%3E%3Crect fill='url(%23d)' width='100%25' height='100%25'/%3E%3Crect fill='url(%23e)' width='100%25' height='100%25'/%3E%3Crect fill='url(%23f)' width='100%25' height='100%25'/%3E%3Crect fill='url(%23g)' width='100%25' height='100%25'/%3E%3C/svg%3E");

      .reliability-rate-badge {
        color: map-get($theme-colors, 'white');

        .high {
          background-color: map-get($theme-colors, 'metric-high');
        }
        .medium {
          background-color: map-get($theme-colors, 'metric-medium');
        }
        .low {
          background-color: map-get($theme-colors, 'red02');
        }
        .veryLow {
          background-color: map-get($theme-colors, 'metric-low');
        }
      }

      .card-body {
        padding-left: 0px;
        padding-right: 0px;
      }

      .card-img-top {
        height: 100px;
        margin-top: 50px;
      }

      &:hover {
        box-shadow: 0 10px 18px 0 rgba(0, 0, 0, 0.2);
      }

      img {
        height: 75px;
      }

      .container {
        margin-top: 20px;
        margin-bottom: 10px;
        padding: 2px 16px;

        .value-label {
          font-size: 0.9em;
          display: flex;
          height: 55px;
          font-weight: bold;
          align-items: center;
          justify-content: center;
        }
      }
    }
  }
}

.card {
  .card-header {
    font-size: 24px;

    .order-by {
      text-transform: uppercase;
    }

    .toggle-sort {
      margin-left: 15px;

      .btn {
        border-radius: 10px !important;
        min-width: 146px !important;

        &.btn-link {
          border: 1px solid map-get($theme-colors, 'primary') !important;
        }
      }
    }
  }
}
</style>
